import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import { exportRegister } from './register-export.js';
import {
  ApiError,
  createMaterial,
  deleteMaterial,
  linkMaterialSubmittal,
  listMaterials,
  listSubmittals,
  moveMaterial,
  updateMaterial,
  type Material,
  type MaterialRisk,
  type MaterialStatus,
  type Submittal,
} from '../api.js';
import { formatDate, formatMoney } from '../labels.js';
import { AppLink } from '../link.js';
import { navigate } from '../router.js';
import { Attachments } from './Attachments.js';
import { CrossLink, focusClass, useFocusRow } from './cross-links.js';
import { Procurement } from './Procurement.js';
import { recordHref, sectionHref } from './project-sections.js';
import { RecordBack, RecordMissing, RecordPage } from './record-page.js';
import {
  Drawer,
  RegisterToolbar,
  RowActions,
  matches,
  useRegisterView,
  type RowAction,
} from './register-kit.js';
import { LOAD_ALL_LIMIT } from '../api/paging.js';

/**
 * The material register (PC-01 / PC-03).
 *
 * The column that matters is **Order by** — the date computed backwards from
 * when the job needs the thing, through lead time, production, shipping and
 * buffer. It is generated by the database, shown here, and never editable:
 * everything upstream of it is, so the way to change it is to change the truth.
 *
 * Each at-risk row says WHY in a sentence, because "red" on its own tells a
 * procurement manager to go and find out, and the point is to have already told
 * them.
 *
 * THIS IS THE REGISTER PATTERN'S FIRST CALLER, AND ONLY ITS FIRST
 * --------------------------------------------------------------
 * The brief's step 7 says to prove the pattern on one register end to end before
 * applying it to twenty-three, so the reusable behaviour is in
 * `register-kit.tsx` and this file is domain: what a material is, which of its
 * moves is the one somebody came to make, what its CSV columns are.
 *
 * WHAT CHANGED HERE, AND WHY EACH ONE
 * -----------------------------------
 *   * **Search, filters, sort, density.** A populated job has a hundred and
 *     forty materials. The register had no way to answer "what is late" except
 *     reading it.
 *   * **One visible row action.** A material can legally move to four statuses
 *     at once; with Edit, Delete and Procurement that was SEVEN controls on one
 *     row — the row nobody reads, which the design system names outright. The
 *     primary action is now the next step in the material's own lifecycle, which
 *     is the repeated act, and everything else is behind "More".
 *   * **A drawer instead of inline forms.** The forms opened ABOVE the table, so
 *     editing row nineteen scrolled the row out of sight and shifted every row
 *     under it. The record and the form are now on screen together.
 *   * **Sticky column headings**, because a register scrolled past its own
 *     headings is six unlabelled columns of dates.
 *   * **Cards on a phone**, not a table scrolled sideways — each cell keeps its
 *     column name through `data-label`, so it stays a table to a screen reader.
 *   * **Export where permitted** (`material: X`). See `toCsv` for why it is
 *     built in the browser and why that is not a gap.
 *
 * NOTHING WAS REMOVED TO MAKE IT TIDIER. Every action, every field and every
 * sentence that was on this screen is still reachable; some of them are one
 * click further away, and the one that got closer is the one people repeat.
 */

const STATUS_LABELS: Readonly<Record<MaterialStatus, string>> = {
  identified: 'Identified',
  blocked_pending_approval: 'Waiting on approval',
  ready_to_order: 'Ready to order',
  ordered: 'Ordered',
  in_production: 'In production',
  shipped: 'Shipped',
  at_site: 'On site',
  inspected: 'Inspected',
  rejected: 'Rejected',
  installed: 'Installed',
  cancelled: 'Cancelled',
};

const STATUS_SYMBOLS: Readonly<Record<MaterialStatus, string>> = {
  identified: '○',
  blocked_pending_approval: '⊘',
  ready_to_order: '◐',
  ordered: '→',
  in_production: '→',
  shipped: '→',
  at_site: '✓',
  inspected: '✓',
  rejected: '✗',
  installed: '✓',
  cancelled: '×',
};

const RISK_WORDS: Readonly<Record<MaterialRisk, string>> = {
  none: 'On track',
  watch: 'Watch',
  amber: 'At risk',
  red: 'Critical',
};

const MOVE_LABELS: Partial<Record<MaterialStatus, string>> = {
  identified: 'Back to identified',
  ready_to_order: 'Ready to order',
  ordered: 'Mark ordered',
  in_production: 'In production',
  shipped: 'Shipped',
  at_site: 'Arrived on site',
  inspected: 'Inspected',
  rejected: 'Reject',
  installed: 'Installed',
  cancelled: 'Cancel',
};

const MIN_REASON = 20;

/**
 * The lifecycle, forwards. Used for ONE thing: picking which of a material's
 * legal next moves is the one that goes on the row.
 *
 * `rejected` and `cancelled` are deliberately absent. Both are legal moves and
 * both stay in the overflow menu — a register whose one-click action is "Cancel"
 * is a register that cancels things by accident, and `05` makes cancelling a
 * decision that demands a written reason.
 */
const FORWARD: readonly MaterialStatus[] = [
  'ready_to_order',
  'ordered',
  'in_production',
  'shipped',
  'at_site',
  'inspected',
  'installed',
];

/**
 * The one move that goes on the row, or nothing.
 *
 * Nothing is a real answer: a material at `installed` has nowhere forward to go,
 * and the row's action is then to open it.
 */
function forwardMove(material: Material): MaterialStatus | null {
  return FORWARD.find((status) => material.allowed_next.includes(status)) ?? null;
}

/** The statuses nothing is waiting on any more. Drives the "outstanding" filter. */
const SETTLED: readonly MaterialStatus[] = ['at_site', 'inspected', 'installed', 'cancelled'];

interface Props {
  readonly projectId: string;
  /** The project's currency. Money labels say it; they never assume AED. */
  readonly currency: string;
  /**
   * One material, as its own page, from
   * `/projects/<id>/procurement/materials/<recordId>`.
   *
   * THE RECORD PAGE IS A MODE OF THE REGISTER, NOT A SECOND SCREEN.
   * ---------------------------------------------------------------
   * Everything the page needs already lives in this component: what a material
   * is, which of its moves is legal, which one demands a written reason, and the
   * two forms that make the changes. A separate `MaterialRecord.tsx` would be a
   * second copy of `move()` and the reason rule — and `05` makes releasing a
   * blocked material an act that is recorded permanently against somebody's
   * name. A rule copied twice is a rule that gets fixed once.
   *
   * The cost is that the page reads the whole register to show one row. That is
   * deliberate: it adds no API surface and therefore no new permission surface,
   * and it is what makes the page's actions provably the register's own. A
   * single-record route is the optimisation to make when a job has enough
   * materials for it to matter.
   */
  readonly recordId?: string | null;
  readonly can: {
    readonly write: boolean;
    readonly edit: boolean;
    readonly remove: boolean;
    /** `material: X`. Gates the export button; the rows are already filtered. */
    readonly exportRows: boolean;
    /** RFQs belong to procurement; deliveries to whoever is at the gate. */
    readonly rfq: boolean;
    readonly deliver: boolean;
  };
}

/**
 * What the drawer is doing. `null` means it is shut.
 *
 * `detail` USED TO BE HERE AND IS GONE. The record now has a page and an
 * address (§4.3), and a record that can be looked at two ways is a record whose
 * two views drift. What is left in the drawer is the three FORMS — add, edit,
 * and the reason a cancellation or an exception demands — because a form is not
 * a record and does not want an address of its own.
 */
type DrawerMode =
  | { readonly kind: 'edit'; readonly id: string }
  | { readonly kind: 'add' }
  | { readonly kind: 'reason'; readonly id: string; readonly to: MaterialStatus };

export function Materials({ projectId, currency, recordId = null, can }: Props): JSX.Element {
  const [items, setItems] = useState<readonly Material[] | null>(null);
  /**
   * How many materials exist on the server, and whether this screen has asked
   * for all of them.
   *
   * Two numbers, deliberately apart: the server bounds the register, and this
   * screen searches and sorts what it has LOADED. Conflating them is what makes
   * a search box quietly answer from a subset.
   */
  const [registerTotal, setRegisterTotal] = useState(0);
  const [wantAll, setWantAll] = useState(false);
  const [submittals, setSubmittals] = useState<readonly Submittal[]>([]);
  /** Not the same as an empty register — see `Drawings.tsx`. */
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState<DrawerMode | null>(null);
  /** Set by the form inside the drawer, so closing it can ask before discarding. */
  const [formDirty, setFormDirty] = useState(false);
  const view = useRegisterView('order_by');

  const load = useCallback(async () => {
    try {
      const page = await listMaterials(projectId, wantAll ? LOAD_ALL_LIMIT : undefined);
      setItems(page.rows);
      setRegisterTotal(page.total);
      setDenied(false);
    } catch (caught) {
      const refused = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setDenied(refused);
      setError(refused ? null : 'Could not load the materials.');
      setItems([]);
    }
    try {
      // A supporting list, not this screen's register: only the rows.
      setSubmittals((await listSubmittals(projectId)).rows);
    } catch {
      setSubmittals([]);
    }
  }, [projectId, wantAll]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<readonly Material[]>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setItems(await work());
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** Shut the drawer, and forget whether its form had edits in it. */
  const closeDrawer = useCallback(() => {
    setDrawer(null);
    setFormDirty(false);
    setError(null);
  }, []);

  /**
   * The rows on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps` — which this repo treats as an
   * error, and rightly: a predicate captured in a hook is a predicate that goes
   * stale silently. It runs before the early returns below because hooks must.
   */
  const shown = useMemo(() => {
    if (!items) return [];

    const standing = view.chosen.standing;
    const stage = view.chosen.stage;

    const filtered = items.filter((m) => {
      // The haystack is everything somebody would type: the reference, what it
      // is, who is supplying it, and the approval it is waiting on.
      const hay = [m.number, m.description, m.supplier_name, m.submittal_number]
        .filter(Boolean)
        .join(' ');
      if (!matches(hay, view.query)) return false;
      if (standing && m.risk !== standing) return false;
      if (stage === 'outstanding' && SETTLED.includes(m.status)) return false;
      if (stage === 'settled' && !SETTLED.includes(m.status)) return false;
      if (stage === 'blocked' && m.status !== 'blocked_pending_approval') return false;
      return true;
    });

    // A null date sorts LAST on every date sort, never first. A material with no
    // date is not the most urgent thing on the job — it is the thing nobody has
    // dated, which belongs at the bottom of a list ordered by urgency.
    const byDate =
      (pick: (m: Material) => string | null) =>
      (a: Material, b: Material): number => {
        const x = pick(a);
        const y = pick(b);
        if (x === y) return 0;
        if (x === null) return 1;
        if (y === null) return -1;
        return x < y ? -1 : 1;
      };

    const RISK_ORDER: Readonly<Record<MaterialRisk, number>> = {
      red: 0,
      amber: 1,
      watch: 2,
      none: 3,
    };

    const compare: Readonly<Record<string, (a: Material, b: Material) => number>> = {
      order_by: byDate((m) => m.order_by_date),
      required: byDate((m) => m.required_on_site_date),
      eta: byDate((m) => m.eta_date),
      risk: (a, b) => RISK_ORDER[a.risk] - RISK_ORDER[b.risk],
      number: (a, b) => a.number.localeCompare(b.number),
    };

    // A copy: `filter` already made one, but saying so stops the next reader
    // wondering whether the server's order is being mutated.
    return [...filtered].sort(compare[view.sortKey] ?? compare.order_by!);
  }, [items, view.query, view.chosen.standing, view.chosen.stage, view.sortKey]);

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Materials</h3>
      {items && can.write && (
        <button type="button" className="inline" onClick={() => setDrawer({ kind: 'add' })}>
          Add material
        </button>
      )}
    </div>
  );

  /*
   * A RECORD PAGE HAS THE SAME FOUR STATES AS THE REGISTER, and they have to
   * stay four different sentences here too — a page that says "not yours" to
   * somebody who is merely early is the exact fault this product shipped five
   * times. What changes is the FURNITURE above them: the register's head carries
   * an "Add material" button, which is the wrong offer to somebody who followed
   * a link to one particular material. They get the way back instead.
   */
  const above = recordId ? <RecordBack projectId={projectId} register="materials" /> : head;

  if (denied) {
    return (
      <>
        {above}
        <p className="hint">Your role does not include this project&rsquo;s materials.</p>
      </>
    );
  }

  if (items === null) {
    return (
      <>
        {above}
        <p aria-live="polite">Loading {recordId ? 'the material' : 'the materials'}…</p>
      </>
    );
  }

  const critical = items.filter((m) => m.risk === 'red');
  const atRisk = items.filter((m) => m.risk === 'amber');
  const outstanding = items.filter((m) => !SETTLED.includes(m.status));

  /** The record the drawer is about. `null` for the add form, which has none. */
  const subject =
    drawer && drawer.kind !== 'add' ? (items.find((m) => m.id === drawer.id) ?? null) : null;

  /**
   * Move a material, asking for a reason first where one is required.
   *
   * Both cases are the same shape and both are in `05`: cancelling stops a
   * material counting against the Procurement gate, and releasing a blocked one
   * is ordering without the approval. Neither may happen on one click.
   */
  function move(material: Material, to: MaterialStatus): void {
    setError(null);
    const needsReason =
      to === 'cancelled' ||
      (material.status === 'blocked_pending_approval' && to === 'ready_to_order');
    if (needsReason) {
      setDrawer({ kind: 'reason', id: material.id, to });
      return;
    }
    void run(() => moveMaterial(projectId, material.id, { status: to }));
  }

  /**
   * Everything that is not the one visible action.
   *
   * The same list serves the row and the record page, because they offer the
   * same moves — that is the point of the page being a mode of this component
   * rather than a screen of its own. Two of the entries differ, and both differ
   * for the same reason: on the page you are already looking at the record.
   */
  function overflowFor(material: Material, onPage = false): readonly RowAction[] {
    const forward = forwardMove(material);
    const actions: RowAction[] = onPage
      ? []
      : [
          {
            label: 'Open',
            onSelect: () => navigate(recordHref(projectId, 'materials', material.id)),
          },
        ];
    if (can.edit) {
      actions.push({ label: 'Edit', onSelect: () => setDrawer({ kind: 'edit', id: material.id }) });
      // Every legal move EXCEPT the one already on the row. The register never
      // offers a move the machine has not returned in `allowed_next`, so a
      // button here cannot be one the service refuses.
      for (const to of material.allowed_next) {
        if (to === forward) continue;
        actions.push({
          label: MOVE_LABELS[to] ?? to,
          danger: to === 'cancelled' || to === 'rejected',
          disabled: busy,
          onSelect: () => move(material, to),
        });
      }
    }
    if (can.remove) {
      actions.push({
        label: 'Delete',
        danger: true,
        disabled: busy,
        onSelect: () => {
          void run(() => deleteMaterial(projectId, material.id)).then((ok) => {
            // Deleting the record you are standing on leaves you standing on an
            // address that names nothing. The register is where the deletion
            // makes sense, so that is where it puts you — rather than on "that
            // material is not here", which is true and reads like a fault.
            if (ok && onPage) navigate(sectionHref(projectId, 'procurement'));
          });
        },
      });
    }
    return actions;
  }

  async function exportRows(): Promise<void> {
    const money = shown.some((m) => 'unit_cost' in m);
    const headers = [
      'Reference',
      'Description',
      'Status',
      'Supplier',
      'Waiting on',
      'Needed on site',
      'Order by',
      'Expected',
      'Standing',
      'Why',
      'Exception',
      // The column is ABSENT for a caller the server did not give money to,
      // rather than present and blank (ADR-005). A blank money column in a
      // spreadsheet reads as zero.
      ...(money ? [`Total cost (${currency})`] : []),
    ];
    const rows = shown.map((m) => [
      m.number,
      m.description,
      STATUS_LABELS[m.status],
      m.supplier_name ?? '',
      m.submittal_number ?? '',
      m.required_on_site_date ?? '',
      m.order_by_date ?? '',
      m.eta_date ?? '',
      RISK_WORDS[m.risk],
      m.risk_reasons.join('; '),
      m.is_exception ? (m.exception_reason ?? 'Yes') : '',
      ...(money ? [m.total_cost ?? ''] : []),
    ]);
    setError(null);
    try {
      // Recorded BEFORE the file is written, and refusable — `09` §Threats
      // wants the row count and the filters. See `register-export.ts`.
      await exportRegister({
        projectId,
        resource: 'material',
        filename: `materials-${projectId.slice(0, 8)}.csv`,
        headers,
        rows,
        filters: { query: view.query, ...view.chosen },
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not export.');
    }
  }

  /**
   * The three forms, shared by the register and the record page.
   *
   * They are the SAME drawers on both, and they have to be: the reason a
   * cancellation demands and the exception a blocked material demands are `05`
   * rules, and a page with its own copy of them is a page that will still be
   * asking for twenty characters after somebody changes the rule to fifty.
   */
  const drawers = (
    <>
      {drawer?.kind === 'add' && (
        <Drawer title="Add a material" dirty={formDirty} onClose={closeDrawer}>
          <MaterialForm
            submittals={submittals}
            currency={currency}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              if (await run(() => createMaterial(projectId, input))) closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'edit' && subject && (
        <Drawer
          title={`Edit ${subject.number}`}
          subtitle={subject.description}
          dirty={formDirty}
          onClose={closeDrawer}
        >
          <MaterialForm
            existing={subject}
            submittals={submittals}
            currency={currency}
            busy={busy}
            onDirtyChange={setFormDirty}
            onCancel={closeDrawer}
            onSubmit={async (input) => {
              const { submittal_id, ...rest } = input;
              // The version this form was OPENED on. These dates feed
              // `order_by_date`, a generated column every procurement risk rule
              // turns on, so a stale save moves a date nobody typed.
              if (
                !(await run(() =>
                  updateMaterial(projectId, subject.id, rest, subject.record_version),
                ))
              )
                return;
              // The link is its own call, because making it is what STOPS the
              // material — so it only fires when it actually changed.
              if ((submittal_id ?? null) !== subject.submittal_id) {
                if (
                  !(await run(() =>
                    linkMaterialSubmittal(projectId, subject.id, submittal_id ?? null),
                  ))
                )
                  return;
              }
              closeDrawer();
            }}
          />
        </Drawer>
      )}

      {drawer?.kind === 'reason' && subject && (
        <Drawer
          title={
            drawer.to === 'cancelled'
              ? `Cancel ${subject.number}`
              : `Order ${subject.number} anyway`
          }
          subtitle={subject.description}
          onClose={closeDrawer}
        >
          <ReasonForm
            material={subject}
            to={drawer.to}
            busy={busy}
            onCancel={closeDrawer}
            onSubmit={async (reason) => {
              if (
                await run(() => moveMaterial(projectId, subject.id, { status: drawer.to, reason }))
              ) {
                closeDrawer();
              }
            }}
          />
        </Drawer>
      )}
    </>
  );

  const alert = error && (
    <div className="alert" role="alert">
      <span>{error}</span>
    </div>
  );

  // ------------------------------------------------------- one material, in full
  if (recordId) {
    const record = items.find((m) => m.id === recordId) ?? null;
    if (!record) {
      /*
       * A FAILED LOAD IS NOT A MISSING RECORD.
       *
       * The load sets `items` to an empty list when the request breaks, so an id
       * that is perfectly valid finds nothing — and "that material is not here"
       * would be a confident, wrong answer about a question nobody could
       * actually ask the server. This product keeps loading, empty,
       * not-permitted and failed as four separate sentences precisely because
       * merging any two of them tells somebody something false; a record page
       * adds a fifth, and it must not swallow the fourth.
       */
      return error ? (
        <>
          {above}
          {alert}
        </>
      ) : (
        <RecordMissing projectId={projectId} register="materials" />
      );
    }

    const forward = forwardMove(record);
    /*
     * The forward move is the accent button on this page — and there genuinely
     * is not always one. An installed material has nowhere further to go, and
     * the honest answer is a page with no primary action rather than a promoted
     * menu item, which on some records would be "Delete".
     *
     * Spread rather than passed as `undefined`: `exactOptionalPropertyTypes` is
     * on in this repo, and it is right — "absent" and "present and undefined"
     * are different things and only one of them is what this means.
     */
    const primary =
      can.edit && forward
        ? {
            label: MOVE_LABELS[forward] ?? forward,
            disabled: busy,
            onSelect: () => move(record, forward),
          }
        : null;

    /*
     * A reader who may do NOTHING to this material gets no action bar at all —
     * not an empty one. A QS and a Client both hold `material: V`, and an empty
     * bordered box where the moves would be is a box that says "there is
     * something here you cannot have".
     */
    const overflow = overflowFor(record, true);
    const actions =
      primary || overflow.length > 0 ? (
        <RowActions
          rowLabel={record.number}
          {...(primary ? { primary } : {})}
          more={overflow}
        />
      ) : null;

    return (
      <>
        {alert}
        <RecordPage
          projectId={projectId}
          register="materials"
          reference={record.number}
          title={record.description}
          status={STATUS_LABELS[record.status]}
          meta={
            <>
              {record.supplier_name ?? 'No supplier yet'}
              {' · '}
              {/*
                The standing, on the head, because it is the one fact that
                decides whether this record is today's problem — and the register
                the reader came from sorts on it. Said in words; the colour on
                `.risk-word` only ever agrees with them.
              */}
              <span className="risk-word">{RISK_WORDS[record.risk]}</span>
            </>
          }
          {...(actions ? { actions } : {})}
          properties={
            <MaterialProperties projectId={projectId} material={record} currency={currency} />
          }
        >
          {/*
            THE MAIN COLUMN IS WHAT THE MATERIAL IS DOING, and both of these are
            lists — which is the whole reason this record outgrew the drawer.
            Mill certificates, delivery notes and damage photos are the paper a
            QS asks for when the invoice arrives; the procurement panel is the
            RFQs, the PO, the deliveries and QA/QC's verdict.
          */}
          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Paperwork</h4>
            </div>
            <Attachments
              projectId={projectId}
              entityType="material_item"
              entityId={record.id}
              can={{ write: can.edit }}
              noun="certificates and delivery notes"
              category="certificate"
            />
          </div>

          <div className="register record-section">
            <div className="section-head sub-head-row">
              <h4 className="sub-head">Procurement</h4>
            </div>
            <Procurement
              projectId={projectId}
              materialId={record.id}
              can={{ rfq: can.rfq, order: can.edit, deliver: can.deliver, inspect: can.edit }}
              // A PO or a verdict moves the material; the page re-reads the
              // register rather than trusting this panel.
              onMaterialChanged={() => void load()}
            />
          </div>
        </RecordPage>
        {drawers}
      </>
    );
  }

  return (
    <>
      {head}

      {alert}

      {items.length > 0 && (
        <p className="hint" role="status">
          <strong>{outstanding.length} still to arrive</strong>
          {critical.length > 0 && (
            <>
              {' · '}
              <span className="task-overdue">{critical.length} critical</span>
            </>
          )}
          {atRisk.length > 0 && <> · {atRisk.length} at risk</>}
          . The Procurement stage cannot close until everything is on site or cancelled.
        </p>
      )}

      {items.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No materials yet.</strong>
          </p>
          <p>
            {can.write
              ? 'Add what has to be on site and when. The date it must be ordered by is worked out backwards from there — through lead time, production, shipping and buffer — and that is the date the project is actually run on.'
              : /*
                 * An empty state must name an action ITS READER can take, and a
                 * Site Engineer or a QS holds `material: V` and no create. This
                 * has shipped wrong six times in this product; the sentence for a
                 * reader who cannot add one says who can.
                 */
                'Nothing has been added yet. Procurement or the Project Manager records what has to be on site and when, and the date it has to be ordered by is worked out backwards from there.'}
          </p>
        </div>
      ) : (
        <>
          <RegisterToolbar
            view={view}
            searchLabel="Search materials"
            searchPlaceholder="Reference, description, supplier"
            shown={shown.length}
            total={items.length}
            registerTotal={registerTotal}
            onLoadAll={() => setWantAll(true)}
            loadingAll={wantAll && items.length < registerTotal}
            noun={['material', 'materials']}
            filters={[
              {
                key: 'stage',
                label: 'Show',
                options: [
                  { value: 'outstanding', label: 'Still to arrive' },
                  { value: 'blocked', label: 'Waiting on approval' },
                  { value: 'settled', label: 'On site or cancelled' },
                ],
              },
              {
                key: 'standing',
                label: 'Standing',
                options: [
                  { value: 'red', label: 'Critical' },
                  { value: 'amber', label: 'At risk' },
                  { value: 'watch', label: 'Watch' },
                  { value: 'none', label: 'On track' },
                ],
              },
            ]}
            sorts={[
              { key: 'order_by', label: 'Order-by date' },
              { key: 'required', label: 'Needed on site' },
              { key: 'eta', label: 'Expected arrival' },
              { key: 'risk', label: 'Standing' },
              { key: 'number', label: 'Reference' },
            ]}
          >
            {can.exportRows && (
              <button type="button" className="secondary inline" onClick={() => void exportRows()}>
                Export {shown.length === items.length ? 'all' : 'these'} to CSV
              </button>
            )}
          </RegisterToolbar>

          {shown.length === 0 ? (
            /*
             * A DIFFERENT empty state from "no materials yet", and the difference
             * matters: this reader has a hundred and forty materials and a filter
             * that excludes all of them. Telling them to add one would be advice
             * about the wrong problem.
             */
            <div className="empty">
              <p>
                <strong>No material matches that.</strong>
              </p>
              <p>
                <button type="button" className="link-button" onClick={view.clear}>
                  Clear the search and filters
                </button>{' '}
                to see all {items.length}.
              </p>
            </div>
          ) : (
            <div className={`rk-scroll${view.density === 'compact' ? ' rk-compact' : ''}`}>
              <table className="grid rk-cards">
                <caption className="visually-hidden">
                  The material register, with the date each has to be ordered by and what is at
                  risk. {shown.length} of {items.length} shown.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Material</th>
                    <th scope="col">Needed on site</th>
                    <th scope="col">Order by</th>
                    <th scope="col">Expected</th>
                    <th scope="col">Standing</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((m) => {
                    const forward = forwardMove(m);
                    return (
                      <MaterialRow key={m.id} material={m}>
                        <td data-label="Material">
                          <span className="ms-symbol" aria-hidden="true">
                            {STATUS_SYMBOLS[m.status]}
                          </span>{' '}
                          {/*
                            The reference is the way IN to the record. A register
                            whose identifier is not clickable makes people hunt
                            for a row action to see the thing they just read.

                            A REAL LINK NOW, and that is the record page earning
                            its keep. It was a `<button>` for an honest reason —
                            it opened a drawer, and `register-kit.css` says a link
                            that changes no address is a link that lies to a
                            middle-click. The record has an address now, so the
                            middle-click, the new tab and the copied link all mean
                            what they look like they mean.
                          */}
                          <AppLink className="rk-open" to={recordHref(projectId, 'materials', m.id)}>
                            <strong>{m.number}</strong> {m.description}
                            <span className="visually-hidden"> — open this material</span>
                          </AppLink>
                          <span className="tree-code"> {STATUS_LABELS[m.status]}</span>
                          {/*
                            SECONDARY. Present at comfortable density, hidden at
                            compact — and every one of these is also in the
                            record's drawer, so nothing is visible at only one
                            density.
                          */}
                          <span className="rk-secondary">
                            {m.supplier_name && <span className="tree-code"> {m.supplier_name}</span>}
                            {m.submittal_number && (
                              <span className="tree-code">
                                {' '}
                                <CrossLink
                                  projectId={projectId}
                                  section="design"
                                  register="submittals"
                                  focus={m.submittal_id}
                                  says={
                                    // "Waiting on" only while it actually is. A
                                    // material already on site is not waiting on
                                    // the approval that released it.
                                    m.status === 'blocked_pending_approval'
                                      ? 'the submittal this material is waiting on'
                                      : `the submittal this material was raised against${m.submittal_status ? `, ${m.submittal_status.replace(/_/g, ' ')}` : ''}`
                                  }
                                >
                                  {m.submittal_number}
                                </CrossLink>
                              </span>
                            )}
                            {m.is_exception && (
                              <span className="mat-exception">
                                Ordered on an exception
                                {m.exception_reason ? `: ${m.exception_reason}` : ''}
                              </span>
                            )}
                            {'unit_cost' in m && m.total_cost && (
                              <span className="tree-code mono">
                                {' '}
                                {m.currency ?? currency} {formatMoney(m.total_cost)}
                              </span>
                            )}
                          </span>
                        </td>
                        <td data-label="Needed on site">{formatDate(m.required_on_site_date)}</td>
                        <td className="mono" data-label="Order by">
                          {formatDate(m.order_by_date)}
                          {m.order_by_date === null && m.required_on_site_date === null && (
                            <span className="hint">{' · '}No date set</span>
                          )}
                        </td>
                        <td data-label="Expected">{formatDate(m.eta_date)}</td>
                        <td data-label="Standing">
                          <span className="risk-word">{RISK_WORDS[m.risk]}</span>
                          <span className="rk-secondary">
                            {m.risk_reasons.map((why) => (
                              <span className="risk-why" key={why}>
                                {why}
                              </span>
                            ))}
                          </span>
                        </td>
                        <td>
                          <RowActions
                            rowLabel={m.number}
                            primary={
                              // The next step in the material's own lifecycle,
                              // which is the act somebody repeats down the
                              // register. With nowhere forward to go, the one
                              // visible action is to open the record.
                              can.edit && forward
                                ? {
                                    label: MOVE_LABELS[forward] ?? forward,
                                    disabled: busy,
                                    onSelect: () => move(m, forward),
                                  }
                                : {
                                    label: 'Open',
                                    onSelect: () =>
                                      navigate(recordHref(projectId, 'materials', m.id)),
                                  }
                            }
                            more={overflowFor(m).filter(
                              // "Open" is already the primary when there is no
                              // forward move; it must not appear twice.
                              (action) => !(action.label === 'Open' && !(can.edit && forward)),
                            )}
                          />
                        </td>
                      </MaterialRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {drawers}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * One row of the register, which knows whether it is the row a cross-link came
 * for. The submittal register links here — "the materials this is holding up" —
 * and landing on the register with nothing marked is landing nowhere.
 */
function MaterialRow({
  material,
  children,
}: {
  readonly material: Material;
  readonly children: React.ReactNode;
}): JSX.Element {
  const { focused, ref } = useFocusRow<HTMLTableRowElement>(material.id);
  return (
    <tr
      ref={ref}
      className={focusClass(focused, `mat-${material.status} risk-${material.risk}`)}
      aria-current={focused ? 'true' : undefined}
    >
      {children}
    </tr>
  );
}

// ---------------------------------------------------------------------------

/**
 * What the material IS — the record page's rail.
 *
 * THIS IS WHERE THE ROW'S SECONDARY CONTENT WENT. The register used to carry an
 * attachment uploader and the procurement panel on every row — a hundred and
 * forty upload controls on a populated job, and a panel that pushed the rest of
 * the register down when it opened. They are on the record now, which is also
 * the only honest way to hide anything at compact density: nothing in this
 * product is visible at one row height and not the other.
 *
 * It was the drawer's whole body until the record earned a page. What changed is
 * only where the parts sit: the FACTS are here, in the rail, and the two lists —
 * the paperwork and the procurement history — are in the main column, laid out
 * side by side instead of stacked in a 480px panel. The fields, and the reasons
 * each one is stated rather than offered as a field, are unchanged.
 */
function MaterialProperties({
  projectId,
  material,
  currency,
}: {
  readonly projectId: string;
  readonly material: Material;
  readonly currency: string;
}): JSX.Element {
  const showsMoney = 'unit_cost' in material;

  return (
    <>
      <dl className="detail-grid">
        <dt>Standing</dt>
        <dd>
          <span className="risk-word">{RISK_WORDS[material.risk]}</span>
          {material.risk_reasons.map((why) => (
            <span className="risk-why" key={why}>
              {why}
            </span>
          ))}
        </dd>

        <dt>Needed on site</dt>
        <dd>{formatDate(material.required_on_site_date)}</dd>

        <dt>Order by</dt>
        {/* Generated by the database from everything above it, and never
            editable — so it is stated here as a fact, not as a field. */}
        <dd className="mono">{formatDate(material.order_by_date)}</dd>

        <dt>Expected</dt>
        <dd>{formatDate(material.eta_date)}</dd>

        <dt>Supplier</dt>
        <dd>{material.supplier_name ?? '—'}</dd>

        <dt>Waiting on</dt>
        <dd>
          {material.submittal_number ? (
            <CrossLink
              projectId={projectId}
              section="design"
              register="submittals"
              focus={material.submittal_id}
              says={
                material.status === 'blocked_pending_approval'
                  ? 'the submittal this material is waiting on'
                  : 'the submittal this material was raised against'
              }
            >
              {material.submittal_number}
            </CrossLink>
          ) : (
            'No approval'
          )}
        </dd>

        {showsMoney && (
          <>
            <dt>Total cost</dt>
            {/* Absent, not blank, for a caller without finance:view (ADR-005) —
                so the whole pair disappears rather than showing an empty figure. */}
            <dd className="mono">
              {material.total_cost ? `${material.currency ?? currency} ${formatMoney(material.total_cost)}` : '—'}
            </dd>
          </>
        )}
      </dl>

      {material.is_exception && (
        <p className="mat-exception">
          Ordered on an exception
          {material.exception_reason ? `: ${material.exception_reason}` : ''}. This is permanent —
          the approval arriving later does not clear it.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

interface ReasonProps {
  readonly material: Material;
  readonly to: MaterialStatus;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (reason: string) => void | Promise<void>;
}

function ReasonForm({ material, to, busy, onCancel, onSubmit }: ReasonProps): JSX.Element {
  const [reason, setReason] = useState('');
  const isException = to === 'ready_to_order';

  return (
    <div className="panel">
      <p className="hint">
        {isException ? (
          <>
            {material.submittal_number ?? 'The submittal'} has not been approved. Releasing this
            for ordering commits money against a decision nobody has made, so it is recorded
            against your name <strong>permanently</strong> — the approval arriving later does not
            clear it.
          </>
        ) : (
          'A cancelled material stops counting against the Procurement stage, so the record has to say why.'
        )}
      </p>

      <div className="field">
        <label htmlFor="mat-reason">
          {isException ? 'Why are you proceeding without it?' : 'Why is it no longer needed?'}
        </label>
        <textarea
          id="mat-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={2000}
          disabled={busy}
          placeholder={
            isException
              ? 'Client instructed us to proceed at risk; long-lead joinery, LOI on file.'
              : 'Client removed the feature wall from scope, variation VO-004 signed.'
          }
        />
        <p className="hint">
          {reason.trim().length}/{MIN_REASON} characters minimum.
        </p>
      </div>

      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || reason.trim().length < MIN_REASON}
          onClick={() => void onSubmit(reason.trim())}
        >
          {busy ? 'Saving…' : isException ? 'Order it anyway' : 'Cancel it'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Back
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface FormValues {
  readonly description: string;
  readonly supplier_name: string | null;
  readonly submittal_id: string | null;
  readonly required_on_site_date: string | null;
  readonly lead_time_days: number;
  readonly production_days: number;
  readonly shipping_days: number;
  readonly buffer_days: number;
  readonly eta_date: string | null;
  readonly unit_cost?: string | null;
  readonly total_cost?: string | null;
}

interface FormProps {
  readonly existing?: Material;
  readonly submittals: readonly Submittal[];
  readonly currency: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: (input: FormValues) => void | Promise<void>;
  /**
   * Told to the drawer, which is what asks before discarding. The form does not
   * own the question because Escape and the backdrop are the drawer's events,
   * and a form that guarded only its own Cancel button would protect the one
   * route out that nobody takes by accident.
   */
  readonly onDirtyChange?: (dirty: boolean) => void;
}

function MaterialForm({
  existing,
  submittals,
  currency,
  busy,
  onCancel,
  onSubmit,
  onDirtyChange,
}: FormProps): JSX.Element {
  const [description, setDescription] = useState(existing?.description ?? '');
  const [supplier, setSupplier] = useState(existing?.supplier_name ?? '');
  const [submittalId, setSubmittalId] = useState(existing?.submittal_id ?? '');
  const [required, setRequired] = useState(existing?.required_on_site_date ?? '');
  const [lead, setLead] = useState(existing?.lead_time_days ?? 0);
  const [production, setProduction] = useState(existing?.production_days ?? 0);
  const [shipping, setShipping] = useState(existing?.shipping_days ?? 0);
  const [buffer, setBuffer] = useState(existing?.buffer_days ?? 0);
  const [eta, setEta] = useState(existing?.eta_date ?? '');
  const [totalCost, setTotalCost] = useState(existing?.total_cost ?? '');

  /** Whether the server let this caller see money at all. */
  const showsMoney = existing ? 'unit_cost' in existing : true;

  /**
   * Whether anything has been typed that a close would throw away.
   *
   * Compared against the record rather than tracked with a flag per field: a
   * flag says "somebody touched this", and somebody who types a character and
   * deletes it has changed nothing and should not be asked to confirm.
   */
  useEffect(() => {
    const dirty =
      description !== (existing?.description ?? '') ||
      supplier !== (existing?.supplier_name ?? '') ||
      submittalId !== (existing?.submittal_id ?? '') ||
      required !== (existing?.required_on_site_date ?? '') ||
      lead !== (existing?.lead_time_days ?? 0) ||
      production !== (existing?.production_days ?? 0) ||
      shipping !== (existing?.shipping_days ?? 0) ||
      buffer !== (existing?.buffer_days ?? 0) ||
      eta !== (existing?.eta_date ?? '') ||
      totalCost !== (existing?.total_cost ?? '');
    onDirtyChange?.(dirty);
  }, [
    description,
    supplier,
    submittalId,
    required,
    lead,
    production,
    shipping,
    buffer,
    eta,
    totalCost,
    existing,
    onDirtyChange,
  ]);

  /**
   * The same arithmetic the database does, shown live as somebody types.
   *
   * A preview, NOT the value — the server's generated column is the number that
   * counts, and this one is thrown away on save. Showing it matters because the
   * order-by date is the point of the form and nobody should have to save to
   * find out they are already late.
   */
  const orderBy = ((): string | null => {
    if (!required) return null;
    const total = (lead || 0) + (production || 0) + (shipping || 0) + (buffer || 0);
    const date = new Date(Date.parse(`${required}T00:00:00Z`) - total * 86_400_000);
    return date.toISOString().slice(0, 10);
  })();

  const today = new Date().toISOString().slice(0, 10);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!description.trim()) return;
    void onSubmit({
      description: description.trim(),
      supplier_name: supplier.trim() || null,
      submittal_id: submittalId || null,
      required_on_site_date: required || null,
      lead_time_days: lead || 0,
      production_days: production || 0,
      shipping_days: shipping || 0,
      buffer_days: buffer || 0,
      eta_date: eta || null,
      ...(showsMoney ? { total_cost: totalCost.trim() || null } : {}),
    });
  }

  const dayField = (
    id: string,
    label: string,
    value: number,
    set: (n: number) => void,
  ): JSX.Element => (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={0}
        max={3650}
        className="diary-count"
        value={value}
        disabled={busy}
        onChange={(e) => set(Math.max(0, Math.trunc(Number(e.target.value) || 0)))}
      />
    </div>
  );

  return (
    <form className="panel" onSubmit={submit}>

      <div className="field-row">
        <div className="field">
          <label htmlFor="mat-description">What is it?</label>
          <input
            id="mat-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={200}
            disabled={busy}
            placeholder="Reception desk joinery"
          />
        </div>
        <div className="field">
          <label htmlFor="mat-supplier">Supplier</label>
          <input
            id="mat-supplier"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            maxLength={200}
            disabled={busy}
            placeholder="The supplier you are buying from"
          />
          <p className="hint">Created the first time you name it, and reused after.</p>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="mat-required">Needed on site by</label>
          <input
            id="mat-required"
            type="date"
            value={required}
            onChange={(e) => setRequired(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="field">
          <label htmlFor="mat-eta">Supplier says it will arrive</label>
          <input
            id="mat-eta"
            type="date"
            value={eta}
            onChange={(e) => setEta(e.target.value)}
            disabled={busy}
          />
          <p className="hint">After the date it is needed, this goes critical.</p>
        </div>
      </div>

      <fieldset className="mat-chain">
        <legend>How long it takes</legend>
        <div className="field-row">
          {dayField('mat-lead', 'Lead time', lead, setLead)}
          {dayField('mat-production', 'Production', production, setProduction)}
          {dayField('mat-shipping', 'Shipping', shipping, setShipping)}
          {dayField('mat-buffer', 'Buffer', buffer, setBuffer)}
        </div>
        <p className={orderBy && orderBy < today ? 'mat-orderby task-overdue' : 'mat-orderby'}>
          {orderBy ? (
            <>
              <strong>Has to be ordered by {formatDate(orderBy)}</strong>
              {orderBy < today && ' — which has already passed'}
            </>
          ) : (
            'Set the date it is needed on site and this works itself out.'
          )}
        </p>
        <p className="hint">
          Worked out backwards from the date it is needed. The server computes the real one on
          save; this is the same sum, shown as you type.
        </p>
      </fieldset>

      <div className="field-row">
        <div className="field">
          <label htmlFor="mat-submittal">Waiting on which approval?</label>
          <select
            id="mat-submittal"
            value={submittalId}
            disabled={busy}
            onChange={(e) => setSubmittalId(e.target.value)}
          >
            <option value="">Not waiting on an approval</option>
            {submittals.map((s) => (
              <option key={s.id} value={s.id}>
                {s.number} {s.title}
              </option>
            ))}
          </select>
          <p className="hint">
            Linking an unapproved submittal stops this material until it is decided. Approving it
            releases this automatically.
          </p>
        </div>
        {showsMoney && (
          <div className="field">
            <label htmlFor="mat-cost">Total cost ({currency})</label>
            <input
              id="mat-cost"
              value={totalCost ?? ''}
              onChange={(e) => setTotalCost(e.target.value)}
              inputMode="decimal"
              disabled={busy}
              placeholder="25000.00"
            />
            <p className="hint">Only people with financial access on this project see it.</p>
          </div>
        )}
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !description.trim()}>
          {busy ? 'Saving…' : existing ? 'Save' : 'Add material'}
        </button>
        <button type="button" className="secondary inline" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
